import React, { useCallback, useEffect, useState } from 'react'
import { Accordion, Alert, Button, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentTypeService } from '../../services/document-type.service'
import { toastService } from '../../services/toast.service'
import { DocumentTemplate, DocumentType } from '../../types'
import { formatDate } from '../../utils/dateFormatter'
import ConfirmDialog from '../common/ConfirmDialog'
import WordTemplateEditorModal, { WordTemplateEditorMode } from './WordTemplateEditorModal'
import './WordTemplatesSection.scss'

interface WordTemplatesSectionProps {
  documentType: DocumentType
}

const WordTemplatesSection: React.FC<WordTemplatesSectionProps> = ({ documentType }) => {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editor, setEditor] = useState<{ templateId: number | null; mode: WordTemplateEditorMode } | null>(null)
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setTemplates(await documentTypeService.getWordTemplates(documentType.id))
    } catch (err: any) {
      setError(err?.response?.data?.error || t('error_loading_data') || 'Greška pri učitavanju šablona.')
    } finally {
      setLoading(false)
    }
  }, [documentType.id, t])

  useEffect(() => {
    void loadTemplates()
  }, [loadTemplates])

  const openEditor = (templateId: number | null, mode: WordTemplateEditorMode) => {
    setEditor({ templateId, mode })
  }

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete || deleting) return
    setDeleting(true)
    try {
      await documentTypeService.deleteWordTemplate(documentType.id, templateToDelete.id)
      await loadTemplates()
      toastService.showSuccess(t('deleted') || 'Šablon je obrisan.')
      setTemplateToDelete(null)
    } catch (err: any) {
      setError(err?.response?.data?.error || t('error_deleting') || 'Greška pri brisanju šablona.')
    } finally {
      setDeleting(false)
    }
  }

  const formatAddedDate = (value: string) => {
    return value ? formatDate(value) : ''
  }

  return (
    <>
      <Accordion className="mt-3 mb-3">
        <Accordion.Item eventKey="word-templates">
          <Accordion.Header>
            <span>
              {t('document_templates') || 'Šabloni dokumenta'} ({templates.length})
            </span>
          </Accordion.Header>
          <Accordion.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-muted small border rounded p-3 mb-3">
                {t('no_templates') || 'Nema dodatih Word šablona.'}
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {templates.map((template) => (
                  <div key={template.id} className="border rounded p-3 d-flex align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center min-width-0">
                      <Button
                        variant="light"
                        className="rounded p-0 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 48, height: 48, backgroundColor: '#eef2ff' }}
                        title={t('reupload_template') || 'Ponovo učitaj šablon'}
                        aria-label={t('reupload_template') || 'Ponovo učitaj šablon'}
                        onClick={() => openEditor(template.id, 'reupload')}
                      >
                        <i className="bi bi-file-earmark-text text-primary fs-4" />
                      </Button>
                      <div className="min-width-0">
                        <div className="fw-semibold text-truncate">{template.original_filename || template.name}</div>
                        <div className="text-muted small">
                          {Object.keys(template.mappings || {}).length} {t('template_mapped_attributes') || 'povezanih atributa'}
                          {formatAddedDate(template.created_at) && <> · {t('template_added') || 'dodato'} {formatAddedDate(template.created_at)}</>}
                        </div>
                      </div>
                    </div>
                    <div className="word-template-actions flex-shrink-0">
                      <Button
                        variant="light"
                        className="word-template-action border rounded"
                        title={t('edit_template') || 'Izmeni šablon'}
                        aria-label={t('edit_template') || 'Izmeni šablon'}
                        onClick={() => openEditor(template.id, 'edit')}
                      >
                        <i className="bi bi-pencil fs-5" />
                      </Button>
                      <Button
                        variant="light"
                        className="word-template-action border rounded text-danger"
                        title={t('delete_template') || 'Obriši šablon'}
                        aria-label={t('delete_template') || 'Obriši šablon'}
                        onClick={() => setTemplateToDelete(template)}
                      >
                        <i className="bi bi-trash fs-5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              variant="outline-primary"
              className="w-100 mt-3 py-2 fw-semibold"
              style={{ borderStyle: 'dashed' }}
              onClick={() => openEditor(null, 'create')}
            >
              <i className="bi bi-plus-lg me-2" />
              {t('add_new_template') || 'Dodaj novi šablon'}
            </Button>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {editor && (
        <WordTemplateEditorModal
          documentType={documentType}
          show
          templateId={editor.templateId}
          mode={editor.mode}
          onClose={() => setEditor(null)}
          onSuccess={() => void loadTemplates()}
        />
      )}

      <ConfirmDialog
        show={!!templateToDelete}
        title={t('delete_template') || 'Obriši šablon'}
        message={t('confirm_delete_template') || 'Obrisati izabrani Word šablon?'}
        confirmText={t('delete') || 'Obriši'}
        onConfirm={() => void confirmDeleteTemplate()}
        onCancel={() => {
          if (!deleting) setTemplateToDelete(null)
        }}
        variant="danger"
        isConfirming={deleting}
      />
    </>
  )
}

export default WordTemplatesSection
